import { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  PageHeader,
  Spinner,
} from '@billda/design-system';
import {
  FolderIcon,
  UploadFileIcon,
  CreateNewFolderOutlinedIcon,
  DeleteOutlineIcon,
  ArrowDropDownIcon,
  DriveFolderUploadOutlinedIcon,
} from '@billda/design-system';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { PageBreadcrumb } from '../../../shared/components/layout/PageBreadcrumb';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { SidePanel, ResizableHandle } from '../../../shared/components/panel';
import { ChipTabs, type ChipTab } from '../../../shared/components/ui/ChipTabs';
import ContextMenu from '../../../shared/components/ui/ContextMenu';
import { useSitePanelState } from '../../sites/hooks/useSitePanelState';
import { FileDetailPanel } from '../../sites/components/files/FileDetailPanel';
import { useAccessibleSites } from '../../sites/hooks/useAccessibleSites';
import { useFolderContents } from '../../sites/hooks/useFolderContents';
import { useCreateFolder } from '../../sites/hooks/useFolderMutations';
import { useFileSelectionStore } from '../../sites/stores/fileSelectionStore';
import { useBulkDelete } from '../../sites/hooks/useBulkDelete';
import { FolderBrowser } from '../../sites/components/files/FolderBrowser';
import { RecycleBinList } from '../../sites/components/files/RecycleBinList';

const tabs: ChipTab[] = [
  { label: 'All Files', value: 'files' },
  { label: 'Recycle Bin', value: 'recycle-bin' },
];

export default function FilesPage() {
  const navigate = useNavigate();
  const { siteId, folderId } = useParams<{
    siteId?: string;
    folderId?: string;
  }>();
  const hasWritePermission = usePermission(Permissions.Files.Write);
  const { data: sites, isLoading } = useAccessibleSites();
  const [activeTab, setActiveTab] = useState<'files' | 'recycle-bin'>('files');
  const { isOpen, type, id: panelId, closePanel } = useSitePanelState();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const numericSiteId = siteId ? parseInt(siteId, 10) : 0;
  const createFolderMutation = useCreateFolder(numericSiteId);
  const bulkDelete = useBulkDelete(numericSiteId);
  const selectedItems = useFileSelectionStore(state => state.selectedItems);
  const getSelectedItems = useFileSelectionStore(
    state => state.getSelectedItems
  );
  const selectionCount = selectedItems.size;

  const handleBulkDelete = () => {
    const items = getSelectedItems();
    const fileIds = items.filter(i => i.type === 'file').map(i => i.id);
    const folderIds = items.filter(i => i.type === 'folder').map(i => i.id);
    bulkDelete.mutate(
      { fileIds, folderIds },
      { onSuccess: () => setDeleteConfirmOpen(false) }
    );
  };

  const handleCreateFolder = useCallback(() => {
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      return;
    }
    createFolderMutation.mutate(
      { name: trimmed, parentFolderId: folderId },
      {
        onSuccess: () => {
          setCreateFolderOpen(false);
          setNewFolderName('');
        },
      }
    );
  }, [newFolderName, folderId, createFolderMutation]);

  const panelContent = (
    <>
      {isOpen && type === 'file' && panelId && siteId && (
        <>
          <ResizableHandle />
          <SidePanel open={isOpen} onClose={closePanel} title="File Details">
            <FileDetailPanel
              fileId={panelId}
              siteId={parseInt(siteId, 10)}
              onClose={closePanel}
            />
          </SidePanel>
        </>
      )}
    </>
  );

  const handleNavigateToSite = useCallback(
    (id: number) => {
      navigate(`/files/${id}`);
    },
    [navigate]
  );

  const handleFolderNavigate = useCallback(
    (newFolderId?: string) => {
      if (newFolderId) {
        navigate(`/files/${siteId}/folders/${newFolderId}`);
      } else {
        navigate(`/files/${siteId}`);
      }
    },
    [navigate, siteId]
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'files' | 'recycle-bin');
  };

  // Folder breadcrumb data — shares React Query cache with FolderBrowser
  const { data: folderData } = useFolderContents(
    numericSiteId,
    siteId ? folderId : undefined
  );

  // Build PageHeader breadcrumb segments from the folder API breadcrumb
  const buildBreadcrumbSegments = () => {
    const segments: { label: string; to?: string }[] = [
      { label: 'Files', to: '/files' },
    ];

    if (!folderData?.breadcrumb) {
      const siteName = sites?.find(s => s.id === numericSiteId)?.name;
      if (siteName) {
        segments.push({ label: siteName });
      }
      return segments;
    }

    for (let i = 0; i < folderData.breadcrumb.length; i++) {
      const item = folderData.breadcrumb[i];
      const isLast = i === folderData.breadcrumb.length - 1;

      if (isLast) {
        segments.push({ label: item.name });
      } else if (item.id === null) {
        segments.push({ label: item.name, to: `/files/${siteId}` });
      } else {
        segments.push({
          label: item.name,
          to: `/files/${siteId}/folders/${item.id}`,
        });
      }
    }

    return segments;
  };

  // Site-level or folder-level view
  if (siteId) {
    return (
      <>
        <Dialog
          open={createFolderOpen}
          onOpenChange={open => {
            if (!open) {
              setCreateFolderOpen(false);
            }
          }}
        >
          <DialogContent className="max-w-xs">
            <DialogHeader>Create folder</DialogHeader>
            <Field label="Folder name">
              <Input
                autoFocus
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
                maxLength={255}
                data-testid="create-folder-name-input"
              />
            </Field>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setCreateFolderOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="neutral"
                onClick={handleCreateFolder}
                disabled={
                  !newFolderName.trim() || createFolderMutation.isPending
                }
              >
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteConfirmOpen}
          onOpenChange={open => {
            if (!open) {
              setDeleteConfirmOpen(false);
            }
          }}
        >
          <DialogContent data-testid="bulk-delete-confirm-dialog">
            <DialogHeader>
              Delete {selectionCount} {selectionCount === 1 ? 'item' : 'items'}?
            </DialogHeader>
            <DialogBody>{`They'll be moved to the recycle bin.`}</DialogBody>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirmOpen(false)}
                data-testid="bulk-delete-cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleBulkDelete}
                disabled={bulkDelete.isPending}
                data-testid="bulk-delete-confirm-button"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PageContentArea isPanelOpen={isOpen} panel={panelContent}>
          <PageHeader
            title={<PageBreadcrumb segments={buildBreadcrumbSegments()} />}
          />
          <PageToolbar
            tabs={
              <ChipTabs
                tabs={tabs}
                value={activeTab}
                onChange={handleTabChange}
              />
            }
            actions={
              activeTab === 'files' && hasWritePermission ? (
                <>
                  <PageHeaderAction
                    label="Delete"
                    icon={<DeleteOutlineIcon className="size-3.5" />}
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={selectionCount === 0}
                    data-testid="bulk-delete-button"
                  />
                  <PageHeaderAction
                    label="New Folder"
                    icon={<CreateNewFolderOutlinedIcon className="size-3.5" />}
                    onClick={() => setCreateFolderOpen(true)}
                    data-testid="create-folder-button"
                  />
                  <PageHeaderAction
                    label="Upload"
                    icon={<UploadFileIcon className="size-3.5" />}
                    endIcon={<ArrowDropDownIcon className="size-3.5" />}
                    onClick={e => setUploadMenuAnchor(e.currentTarget)}
                    data-testid="upload-file-button"
                    aria-haspopup="true"
                    aria-expanded={Boolean(uploadMenuAnchor)}
                  />
                </>
              ) : undefined
            }
            hideFilter
          />
          <ContextMenu
            open={Boolean(uploadMenuAnchor)}
            onClose={() => setUploadMenuAnchor(null)}
            anchorEl={uploadMenuAnchor}
            items={[
              {
                key: 'upload-files',
                label: 'Upload files',
                icon: <UploadFileIcon className="size-4" />,
                onClick: () => fileInputRef.current?.click(),
              },
              {
                key: 'upload-folder',
                label: 'Upload folder',
                icon: <DriveFolderUploadOutlinedIcon className="size-4" />,
                onClick: () => folderInputRef.current?.click(),
              },
            ]}
          />
          {activeTab === 'files' ? (
            <FolderBrowser
              siteId={numericSiteId}
              folderId={folderId}
              onNavigate={handleFolderNavigate}
              onNavigateUp={() => navigate('/files')}
              canWrite={hasWritePermission}
              uploadButtonRef={fileInputRef}
              folderInputRef={folderInputRef}
              hideBreadcrumb
              onCreateFolder={() => setCreateFolderOpen(true)}
            />
          ) : (
            <RecycleBinList siteId={numericSiteId} />
          )}
        </PageContentArea>
      </>
    );
  }

  // Top-level site list
  return (
    <PageContentArea isPanelOpen={false}>
      <PageHeader title="Files" />
      <PageToolbar
        tabs={
          <ChipTabs tabs={tabs} value={activeTab} onChange={handleTabChange} />
        }
        hideFilter
      />
      {activeTab === 'recycle-bin' ? (
        <RecycleBinList />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size={32} />
        </div>
      ) : !sites || sites.length === 0 ? (
        <div className="py-12 text-center">
          <p className="type-body-md text-content-secondary">
            No sites available
          </p>
        </div>
      ) : (
        <div>
          {sites.map(site => (
            <button
              type="button"
              key={site.id}
              onClick={() => handleNavigateToSite(site.id)}
              className="flex w-full items-center gap-3 border-b border-border-default px-4 py-3 text-left sm:px-6"
              data-testid={`site-row-${site.id}`}
            >
              <FolderIcon
                aria-hidden="true"
                className="size-5 text-content-secondary"
              />
              <span className="type-body-md min-w-0 flex-1 truncate">
                {site.name}
              </span>
              {site.lastModified && (
                <span className="type-caption shrink-0 text-content-secondary">
                  {new Date(site.lastModified).toLocaleDateString()}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </PageContentArea>
  );
}
